import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  useTheme,
  FormHelperText,
  useMediaQuery,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton
} from "@mui/material";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

// project imports
import AnimateButton from "../../../ui-component/extended/AnimateButton";
import useQuery from "../../../hooks/useQuery";
import { DocumentService } from "../../../services/intranet-portal-services";
import DatePickerCustom from "../../../ui-component/CustomUiComponent/DatePicker";

// third party
import * as Yup from "yup";
import { ErrorMessage, Form, Formik } from "formik";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
// import { useDropzone } from "react-dropzone";
import styled from "@emotion/styled";
import usePageTracking from "../../../utils/withPageTracking";

const FORM_VALIDATION = Yup.object().shape({
  name: Yup.string().required("Name is required *"),
  acknowledgementDeadlineInDays: Yup.string().required("Acknowledge deadline date is required *"),
  description: Yup.string().required("Description is required *")
});

const MAX_UPLOAD_FILE_SIZE = 30 * 1024 * 1024;

interface PreviewFile extends File {
  preview: string;
}

const AddDocument = (): React.ReactElement => {
  const navigate = useNavigate();
  const theme = useTheme<any>();
  const [getEditDocumentData, setGetEditDocumentData] = useState<any>();
  const [addFileResponse, setAddFileResponse] = useState<any>();
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const params = useQuery();
  const id: any = params.get("id");

  const initialState = {
    name: "",
    description: "",
    documentUrl: addFileResponse,
    acknowledgementDeadlineInDays: ""
  };

  const handleDocumentApp = async (val: any): Promise<void> => {
    const formData = {
      name: val.name,
      description: val.description,
      documentUrl: addFileResponse,
      acknowledgementDeadlineInDays: val.acknowledgementDeadlineInDays
    };

    if (id != null) {
      try {
        const editDocumentApp = await DocumentService.putCoreApiApiAppDocumentDocument(
          id,
          formData
        );
        if (editDocumentApp != null) {
          toast.success("Document updated successfully");
          void navigate(-1);
        }
      } catch (e: any) {
        if (e.status != null) {
          toast.error(e.status);
        } else if (e?.body?.error?.validationErrors != null) {
          toast.error(e?.body?.error?.validationErrors);
        } else if (e.message != null) {
          toast.error(e.message);
        } else {
          toast.error("Unable to fetch api");
        }
      }
    } else {
      try {
        const addresponse = await DocumentService.postCoreApiApiAppDocumentDocument(formData);
        if (addresponse != null) {
          toast.success("Document added successfully");
          void navigate(-1);
        }
      } catch (e: any) {
        if (e.status != null) {
          toast.error(e.status);
        } else if (e?.body?.error?.validationErrors != null) {
          toast.error(e?.body?.error?.validationErrors);
        } else if (e.message != null) {
          toast.error(e.message);
        } else {
          toast.error("Unable to fetch api");
        }
      }
    }
  };

  const fetchEditData = async (): Promise<void> => {
    try {
      const editResponse = await DocumentService.getCoreApiApiAppDocumentDocumentById(id);
      setGetEditDocumentData(editResponse);
    } catch (e: any) {
      if (e.status != null) {
        toast.error(e.status);
      } else if (e?.body?.error?.validationErrors != null) {
        toast.error(e?.body?.error?.validationErrors);
      } else if (e.message != null) {
        toast.error(e.message);
      } else {
        toast.error("Unable to fetch api");
      }
    }
  };

  useEffect(() => {
    if (id != null) {
      void fetchEditData();
    }
  }, []);

  const handleDocumentLogo = async (val: File): Promise<void> => {
    try {
      const formDataFile = {
        file: val
      };
      const addFileResponse =
        await DocumentService.postCoreApiApiAppDocumentUploadDocument(formDataFile);
      if (addFileResponse != null) {
        setAddFileResponse(addFileResponse);
        toast.success("File uploaded successfully");
      }
    } catch (e: any) {
      /* if (e.status) {
        toast.error(e.status);
      } else */ /* if (e?.body?.error?.validationErrors) {
        toast.error(e?.body?.error?.validationErrors);
      } else */ if (e.message != null) {
        toast.error("Only PDF or PNG files up to 30MB are supported");
      } else {
        toast.error("Unable to fetch api");
      }
    }
  };

  const validateSelectedFile = (selectedFile: File): boolean => {
    const allowedTypes = ["application/pdf", "image/png"];
    const isAllowedType = allowedTypes.includes(selectedFile.type);
    const isAllowedSize = selectedFile.size <= MAX_UPLOAD_FILE_SIZE;

    if (!isAllowedType || !isAllowedSize) {
      toast.error("Only PDF or PNG files up to 30MB are supported");
      return false;
    }

    return true;
  };

  const handleSelectedFile = (selectedFile: File): void => {
    if (!validateSelectedFile(selectedFile)) {
      return;
    }

    setFiles([
      Object.assign(selectedFile, {
        preview: URL.createObjectURL(selectedFile)
      }) as PreviewFile
    ]);

    void handleDocumentLogo(selectedFile);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile == null) {
      return;
    }

    handleSelectedFile(selectedFile);
    event.target.value = "";
  };

  const handleDropzoneClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const selectedFile = event.dataTransfer.files[0];

    if (selectedFile == null) {
      return;
    }

    handleSelectedFile(selectedFile);
  };

  const removeFile = (name: string): void => {
    setFiles((currentFiles) => {
      const fileToRemove = currentFiles.find((file) => file.name === name);

      if (fileToRemove != null) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return currentFiles.filter((file) => file.name !== name);
    });
    setAddFileResponse(undefined);
  };

  const Dropzone = styled("div")({
    flex: "1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: "2px",
    borderRadius: "2px",
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    cursor: "pointer",
    transition: "border .24s ease-in-out",
    "&:hover": {
      borderColor: "#c62828"
    }
  });

  usePageTracking();

  return (
    <>
      <Box>
        <Grid container spacing={2} mb={2} alignItems={"center"}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: "#D42C2C" }}>
              {id != null ? "Update" : "Add"} Document
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            {/* Logo Upload Area of document app */}
            {id == null && (
              <Box>
                <Dropzone
                  onClick={handleDropzoneClick}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  aria-label="Select file"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png"
                    onChange={handleFileInputChange}
                    style={{ display: "none" }}
                  />
                  <p>click to select files</p>
                </Dropzone>
                <Box>
                  <>
                    {files?.map((file: PreviewFile, index: number) =>
                      file.type === "application/pdf" ? (
                        <Typography
                          sx={{ display: "flex", margin: "20px" }}
                          variant="h5"
                          key={index}
                        >
                          {`${file.name} uploaded`}

                          <HighlightOffIcon
                            onClick={() => {
                              removeFile(file.name);
                            }}
                            sx={{ fontSize: "25px", marginLeft: "10px" }}
                            key={index}
                          />
                        </Typography>
                      ) : (
                        <ImageList cols={8} key={index}>
                          <ImageListItem key={file.name}>
                            <img
                              src={file.preview}
                              alt={file.name}
                              width={100}
                              height={100}
                              onLoad={() => {
                                URL.revokeObjectURL(file.preview);
                              }}
                              loading="lazy"
                            />
                            <ImageListItemBar
                              sx={{
                                background:
                                  "linear-gradient(to top, rgba(0,0,0,0.7) 0%, " +
                                  "rgba(0,0,0,0.6) 70%, rgba(0,0,0,0) 100%)"
                              }}
                              position="bottom"
                              actionIcon={
                                <IconButton sx={{ color: "white" }}>
                                  <HighlightOffRoundedIcon sx={{ fontSize: "24px" }} />
                                </IconButton>
                              }
                              actionPosition="right"
                              onClick={() => {
                                removeFile(file.name);
                              }}
                            />
                          </ImageListItem>
                        </ImageList>
                      )
                    )}
                  </>
                </Box>
              </Box>
            )}
            {/* Form post of internal app */}
            <Formik
              initialValues={
                id != null
                  ? {
                      name: getEditDocumentData?.name ?? "",
                      description: getEditDocumentData?.description ?? "",
                      documentUrl: getEditDocumentData?.documentUrl ?? "",
                      acknowledgementDeadlineInDays:
                        getEditDocumentData?.creationTime != null &&
                        getEditDocumentData?.acknowledgementDeadlineInDays != null
                          ? dayjs(getEditDocumentData.creationTime)
                              .add(getEditDocumentData.acknowledgementDeadlineInDays, "days")
                              .format("YYYY-MM-DD")
                          : ""
                    }
                  : initialState
              }
              onSubmit={handleDocumentApp}
              enableReinitialize={id}
              validationSchema={FORM_VALIDATION}
            >
              {({ handleChange, values, setFieldValue }) => (
                <Form noValidate>
                  <Grid container spacing={matchDownSM ? 0 : 2} marginY={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        margin="none"
                        name="name"
                        type="text"
                        value={values.name}
                        onChange={handleChange}
                        autoComplete="off"
                        variant="outlined"
                      />
                      <FormHelperText
                        variant="outlined"
                        sx={{ fontSize: "1rem" }}
                        error
                        id="standard-weight-helper-text--register"
                      >
                        <ErrorMessage name="name" />
                      </FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePickerCustom
                        label="Acknowledge Deadline Date"
                        selected={values.acknowledgementDeadlineInDays || null}
                        onChange={(val: any) => {
                          void setFieldValue(
                            "acknowledgementDeadlineInDays",
                            val != null ? dayjs(val).format("YYYY-MM-DD") : ""
                          );
                        }}
                      />
                      <FormHelperText
                        variant="outlined"
                        sx={{ fontSize: "1rem" }}
                        error
                        id="standard-weight-helper-text--register"
                      >
                        <ErrorMessage name="acknowledgementDeadlineInDays" />
                      </FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Description"
                        margin="none"
                        name="description"
                        type="text"
                        onChange={handleChange}
                        rows={3}
                        value={values.description}
                        autoComplete="off"
                        variant="outlined"
                      />
                      <FormHelperText
                        variant="outlined"
                        sx={{ fontSize: "1rem" }}
                        error
                        id="standard-weight-helper-text--register"
                      >
                        <ErrorMessage name="description" />
                      </FormHelperText>
                    </Grid>
                    <Grid container spacing={matchDownSM ? 0 : 2}>
                      {id == null && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            sx={{ display: "none" }}
                            fullWidth
                            label={
                              addFileResponse === null || getEditDocumentData?.documentUrl === null
                                ? ""
                                : "Document Url"
                            }
                            margin="none"
                            name="documentUrl"
                            type="text"
                            value={addFileResponse}
                            onChange={handleChange}
                            autoComplete="off"
                            variant="outlined"
                            disabled
                          />
                          <FormHelperText
                            variant="outlined"
                            sx={{ fontSize: "1rem" }}
                            error
                            id="standard-weight-helper-text--register"
                          >
                            <ErrorMessage name="documentUrl" />
                          </FormHelperText>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <AnimateButton>
                      <Button
                        disableElevation
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="error"
                      >
                        {id != null ? "Update" : "Add"}
                      </Button>
                    </AnimateButton>
                  </Box>
                </Form>
              )}
            </Formik>
            <AnimateButton>
              <Button
                sx={{ mt: 2 }}
                disableElevation
                fullWidth
                size="large"
                type="submit"
                variant="outlined"
                color="error"
                onClick={() => {
                  void navigate(-1);
                }}
              >
                Back To the list
              </Button>
            </AnimateButton>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};
export default AddDocument;
